'use client';

import { c as _c } from "react/compiler-runtime";
import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { cn, formatNumber } from '@/lib/utils';
import { stripHandlePrefixes } from '@/lib/utils/regex';
import type { CompareEngagementTimeMetric, CompareEngagementToolResultData } from '../types';
import { formatAverageMetric, formatComparisonPeriod, sortPeriods } from './compare-engagement-helpers';
import { EmptyResult } from './GenericObjectResult';
export interface CompareEngagementResultCardProperties {
  result: CompareEngagementToolResultData;
}
export function CompareEngagementResultCard(t0) {
  const $ = _c(28);
  const {
    result
  } = t0;
  if (result.metrics.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <EmptyResult message="No engagement data available for that request." />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (result.type === "users") {
    let t1;
    if ($[1] !== result.metrics) {
      t1 = toSortedCompat(result.metrics, _temp);
      $[1] = result.metrics;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const sorted = t1;
    const leader = sorted[0]?.username ?? null;
    const t2 = result.metrics.length === 1 ? "account" : "accounts";
    let t3;
    if ($[3] !== result.metrics.length || $[4] !== t2) {
      t3 = <div className="text-xs text-muted-foreground">Comparing {result.metrics.length} {t2} based on recent timeline performance.</div>;
      $[3] = result.metrics.length;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== leader || $[7] !== result.metrics) {
      let t5;
      if ($[9] !== leader) {
        t5 = metric => {
          const isLeader = leader === metric.username;
          return <div key={metric.username} className={cn("rounded-lg border border-border/60 bg-card/75 p-3 shadow-sm transition-colors", isLeader && "border-primary/60 bg-primary/5")}><div className="flex items-center justify-between text-sm font-semibold text-foreground"><span>@{stripHandlePrefixes(metric.username)}</span>{isLeader ? <span className="text-xs font-medium text-primary">Top performer</span> : null}</div><div className="mt-3 grid grid-cols-3 gap-2 text-xs text-muted-foreground"><MetricPill label="Avg likes" value={formatAverageMetric(metric.avgLikes)} /><MetricPill label="Avg retweets" value={formatAverageMetric(metric.avgRetweets)} /><MetricPill label="Tweets" value={formatNumber(metric.totalTweets)} /></div></div>;
        };
        $[9] = leader;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      t4 = result.metrics.map(t5);
      $[6] = leader;
      $[7] = result.metrics;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[11] !== t4) {
      t5 = <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{t4}</div>;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== t3 || $[14] !== t5) {
      t6 = <div className="space-y-3 text-sm">{t3}{t5}</div>;
      $[13] = t3;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    return t6;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[16] !== result.metrics) {
    const sortedPeriods = sortPeriods(result.metrics);
    let leader_0 = null;
    for (const metric_0 of sortedPeriods) {
      if (!leader_0 || metric_0.avgLikes > leader_0.avgLikes) {
        leader_0 = metric_0;
      }
    }
    t3 = "space-y-3 text-sm";
    t4 = leader_0 ? <div className="text-xs text-muted-foreground">Engagement across periods (top average likes: {formatComparisonPeriod(leader_0.period)}).</div> : <div className="text-xs text-muted-foreground">No engagement data available for this timeline.</div>;
    t1 = "space-y-2";
    t2 = sortedPeriods.map(metric_1 => {
      const isLeader_0 = leader_0 ? leader_0.period === metric_1.period : false;
      return <div key={metric_1.period} className={cn("flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-card/70 px-3 py-2 text-xs text-muted-foreground", isLeader_0 && "border-primary/60 bg-primary/5 text-foreground")}><div className="font-medium text-foreground">{formatComparisonPeriod(metric_1.period)}</div><div className="flex flex-wrap items-center gap-3"><MetricInline label="Avg likes" value={formatAverageMetric(metric_1.avgLikes)} /><MetricInline label="Avg retweets" value={formatAverageMetric(metric_1.avgRetweets)} /><MetricInline label="Tweets" value={formatNumber(metric_1.totalTweets)} /></div></div>;
    });
    $[16] = result.metrics;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
  } else {
    t1 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
  }
  let t5;
  if ($[21] !== t1 || $[22] !== t2) {
    t5 = <div className={t1}>{t2}</div>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t3 || $[25] !== t4 || $[26] !== t5) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
}
function _temp(a, b) {
  return b.avgLikes - a.avgLikes;
}
function MetricPill(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="text-[0.65rem] uppercase tracking-wide">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <div className="text-sm font-semibold text-foreground">{value}</div>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-md border border-border/40 bg-muted/40 px-2.5 py-1.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function MetricInline(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="font-medium text-foreground/90">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span>{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}